import { Link } from 'react-router-dom';
import './ForHouseholdsPage.css';

const STEPS = [
  {
    n: '01',
    title: 'Post your scrap',
    body: 'Snap a few photos, add your postcode and a short description. Listing is free and takes a couple of minutes.',
  },
  {
    n: '02',
    title: 'Get matched locally',
    body: 'Nearby licensed collectors review your post and arrange a convenient collection window with you.',
  },
  {
    n: '03',
    title: 'Cleared the responsible way',
    body: 'Your scrap is collected, documented, and handled through licensed routes — not fly-tipped.',
  },
];

const ITEMS = [
  { title: 'White goods', body: 'Fridges, washers, cookers, and other metal appliances ready for collection.' },
  { title: 'Garden metal', body: 'Old bikes, fencing, tools, and shed clear-outs from home renovations.' },
  { title: 'DIY leftovers', body: 'Pipes, radiators, cables, brackets, and leftover building metals.' },
  { title: 'Garage clear-outs', body: 'Mixed scrap piles, broken equipment, and metal clutter taking up space.' },
  { title: 'Single items', body: 'One heavy piece or a small batch — post what you need gone.' },
  { title: 'House moves', body: 'Clear metal waste before a move-in, move-out, or property tidy-up.' },
];

const BENEFITS = [
  {
    title: 'Free to post',
    body: 'Create a listing at no cost and only proceed when a collector suits you.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v8M9.5 10.5h3.2a1.8 1.8 0 1 1 0 3.6H9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Licensed collectors',
    body: 'Matched collectors are verified scrap carriers — not unlicensed van ads.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3.5l7 2.5v5.2c0 4.3-2.9 7.3-7 9.3-4.1-2-7-5-7-9.3V6L12 3.5z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path d="M9.2 12.1l1.9 1.9 3.7-3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Home-friendly times',
    body: 'Arrange collection around your schedule — evenings and weekends often available.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v4.5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Clear communication',
    body: 'Share access notes, photos, and item details so collectors arrive prepared.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v6A2.5 2.5 0 0 1 16.5 16H10l-4 3v-3H7.5A2.5 2.5 0 0 1 5 13.5v-6z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const TIPS = [
  'Group similar metals together if you can — it speeds up collection.',
  'Add clear photos from a few angles and note stairs or parking limits.',
  'Keep items accessible on collection day so the team can load safely.',
  'Ask for confirmation of licensed handling if you need paperwork for records.',
];

export default function ForHouseholdsPage() {
  return (
    <div className="households-page">
      <section className="households-hero" aria-labelledby="households-heading">
        <div className="households-hero-inner">
          <p className="households-kicker">SnapScrap for homes</p>
          <h1 id="households-heading">For Households</h1>
          <p>
            Clear scrap metal from your home the simple way. Post what you need collected and get
            matched with licensed local collectors — free to list, easy to arrange.
          </p>
          <div className="households-hero-actions">
            <Link className="households-btn" to="/post-scrap">
              Post Scrap →
            </Link>
            <Link className="households-btn households-btn-secondary" to="/what-happens-to-your-scrap">
              See what happens next
            </Link>
          </div>
        </div>
      </section>

      <section className="households-section" aria-labelledby="households-steps-heading">
        <div className="households-inner">
          <header className="households-header">
            <h2 id="households-steps-heading">How it works at home</h2>
            <p>Three simple steps from clutter to a cleared driveway</p>
          </header>
          <ol className="households-steps">
            {STEPS.map((step) => (
              <li key={step.n}>
                <span className="households-step-n" aria-hidden="true">
                  {step.n}
                </span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="households-section households-section-alt" aria-labelledby="households-items-heading">
        <div className="households-inner">
          <header className="households-header">
            <h2 id="households-items-heading">What households usually post</h2>
            <p>From a single appliance to a full garage clear-out</p>
          </header>
          <ul className="households-items">
            {ITEMS.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="households-section" aria-labelledby="households-benefits-heading">
        <div className="households-inner">
          <header className="households-header">
            <h2 id="households-benefits-heading">Built for busy households</h2>
            <p>Straightforward collection without the hassle of skip hire or tip runs</p>
          </header>
          <ul className="households-benefits">
            {BENEFITS.map((item) => (
              <li key={item.title}>
                <span className="households-benefit-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="households-section households-section-alt" aria-labelledby="households-tips-heading">
        <div className="households-inner households-tips-wrap">
          <header className="households-header">
            <h2 id="households-tips-heading">Quick tips before you post</h2>
            <p>A little prep helps collectors arrive ready and finish faster</p>
          </header>
          <ul className="households-tips">
            {TIPS.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="households-cta-section" aria-label="Post scrap call to action">
        <div className="households-inner">
          <div className="households-cta">
            <h2>Ready to clear your scrap?</h2>
            <p>Post for free today and get matched with a licensed collector near you.</p>
            <div className="households-hero-actions">
              <Link className="households-btn" to="/post-scrap">
                Post Scrap →
              </Link>
              <Link className="households-btn households-btn-secondary" to="/contact">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
